import * as React from 'react';
import { StepManagerFooter } from './step-manager-footer';
import { StepManagerHeader } from './step-manager-header';
import type {
	BeforeContinueGate,
	IStepDefinition,
	IStepManagerContext,
	IStepState,
	StepManagerProps,
} from './step-manager.types';
import { cn } from '@utils';

const StepManagerContext = React.createContext<IStepManagerContext | null>(null);

export const StepManager = <TStepId extends string>({
	steps,
	initialStepId,
	onComplete,
	onStepChange,
	className,
}: StepManagerProps<TStepId>): JSX.Element => {
	const [currentStepId, setCurrentStepId] = React.useState<TStepId>(initialStepId ?? steps[0].id);

	const [isLoading, setIsLoading] = React.useState(false);

	const isMountedRef = React.useRef(false);

	React.useEffect(() => {
		if (!isMountedRef.current) {
			isMountedRef.current = true;
			return;
		}
		onStepChange?.(currentStepId);
	}, [currentStepId, onStepChange]);

	const [stepValidityMap, setStepValidityMap] = React.useState<Record<string, boolean>>({});

	const currentStepIndex: number = steps.findIndex(s => s.id === currentStepId);

	const currentStep: IStepDefinition<TStepId> = steps[currentStepIndex];

	const goTo = React.useCallback((stepId: string): void => setCurrentStepId(stepId as TStepId), [setCurrentStepId]);

	const next = React.useCallback((): void => {
		const nextIndex: number = currentStepIndex + 1;
		if (nextIndex < steps.length) {
			setCurrentStepId(steps[nextIndex].id);
		}
	}, [currentStepIndex, steps]);

	const goBack = React.useCallback((): void => {
		const { back } = currentStep;

		if (typeof back === 'function') {
			return back();
		}

		if (back) {
			setCurrentStepId(back);
		}
	}, [currentStep, setCurrentStepId]);

	const setStepValid = React.useCallback((stepId: string, isValid: boolean): void => {
		setStepValidityMap(prev => {
			if (prev[stepId] === isValid) {
				return prev;
			}

			return { ...prev, [stepId]: isValid };
		});
	}, []);

	const stepValidityMapRef: React.MutableRefObject<Record<string, boolean>> = React.useRef(stepValidityMap);
	stepValidityMapRef.current = stepValidityMap;

	const beforeContinueMapRef: React.MutableRefObject<Record<string, BeforeContinueGate>> = React.useRef({});

	const registerBeforeContinue = React.useCallback((stepId: string, gate: BeforeContinueGate): void => {
		beforeContinueMapRef.current[stepId] = gate;
	}, []);

	const unregisterBeforeContinue = React.useCallback((stepId: string): void => {
		delete beforeContinueMapRef.current[stepId];
	}, []);

	const handleContinue = React.useCallback(async (): Promise<void> => {
		if (!stepValidityMapRef.current[currentStepId]) {
			return;
		}

		const gate = beforeContinueMapRef.current[currentStepId];

		if (gate) {
			let canContinue: boolean = true;

			try {
				const result: Promise<boolean> | boolean = gate();

				if (result instanceof Promise) {
					setIsLoading(true);
					canContinue = await result.finally(() => setIsLoading(false));
				}

				if (typeof result === 'boolean') {
					canContinue = result;
				}
			} catch {
				// fail open — gate error should not strand the user
			}

			if (!canContinue) {
				return;
			}
		}

		const isLastStep: boolean = currentStepIndex === steps.length - 1;
		if (!isLastStep) {
			goTo(steps[currentStepIndex + 1].id);
			return;
		}

		const result = onComplete?.();

		if (result instanceof Promise) {
			setIsLoading(true);
			result.finally(() => {
				setIsLoading(false);
			});
		}
	}, [currentStepId, currentStepIndex, steps, onComplete, goTo]);

	const isCurrentStepValid: boolean = Boolean(stepValidityMap[currentStepId]);

	const showBack: boolean = Boolean(currentStep.back);

	const contextValue: IStepManagerContext = {
		currentStepId,
		goTo,
		goBack,
		next,
		setStepValid,
		registerBeforeContinue,
		unregisterBeforeContinue,
	};

	const CurrentStepComponent = currentStep.component;

	return (
		<StepManagerContext.Provider value={contextValue}>
			<div className={cn('flex h-full flex-col', className)}>
				<div className="shrink-0 px-5 pt-4 pb-2">
					<StepManagerHeader currentIndex={currentStepIndex} totalSteps={steps.length} />
				</div>
				<div className="min-h-0 flex-1 overflow-y-auto">
					<CurrentStepComponent id={currentStep.id} />
				</div>
				<StepManagerFooter
					isValid={isCurrentStepValid}
					showBack={showBack}
					onContinue={handleContinue}
					onBack={goBack}
					continueLabel={currentStep.continueLabel}
					isLoading={isLoading}
				/>
			</div>
		</StepManagerContext.Provider>
	);
};

StepManager.displayName = 'StepManager';

export function useStep(id: string, validate?: () => boolean): IStepState {
	const ctx = React.useContext(StepManagerContext);

	if (!ctx) {
		throw new Error('useStep must be used within a StepManager');
	}

	const isValid: boolean = validate ? validate() : true;

	const { setStepValid } = ctx;

	React.useLayoutEffect(() => {
		setStepValid(id, isValid);
	}, [isValid, id, setStepValid]);

	return {
		isActive: ctx.currentStepId === id,
		isValid,
	};
}

export function useStepManager(): IStepManagerContext {
	const ctx = React.useContext(StepManagerContext);

	if (!ctx) {
		throw new Error('useStepManager must be used within a StepManager');
	}

	return ctx;
}

export function useBeforeContinue(stepId: string, gate: BeforeContinueGate): void {
	const { registerBeforeContinue, unregisterBeforeContinue } = useStepManager();

	React.useEffect(() => {
		registerBeforeContinue(stepId, gate);

		return () => unregisterBeforeContinue(stepId);
	}, [stepId, gate, registerBeforeContinue, unregisterBeforeContinue]);
}
